/**
 * Talking to the room directory.
 *
 * The wire shape is shared with `server/directory-core.ts` by hand rather than
 * by import: this file ships to the browser and that one runs on the Worker,
 * and the only thing they have in common is two small JSON shapes.
 */

export interface RoomSummary {
  code: string
  name: string
  players: number
  capacity: number
  started: boolean
  joinable: boolean
}

/**
 * The relay's HTTP origin, derived from its WebSocket URL.
 *
 * From the origin, not by trimming the string: the relay endpoint is a path
 * (`/join`), and trimming it produced `/join/rooms`.
 */
export function relayOrigin(relayUrl: string): string {
  return new URL(relayUrl.replace(/^ws/, 'http')).origin
}

export interface RoomListing {
  rooms: RoomSummary[]
  /** People with the game open, this client included. */
  online: number
}

/**
 * An id for this tab, used only to count how many people are here.
 *
 * Session storage, not local storage: it should not outlive the tab, and two
 * tabs of the game are two people as far as a presence count can tell. It is
 * random and means nothing to anyone but the counter.
 */
function clientId(): string {
  const KEY = 'bluealert.client'
  try {
    const existing = sessionStorage.getItem(KEY)
    if (existing) return existing
    const fresh = crypto.randomUUID()
    sessionStorage.setItem(KEY, fresh)
    return fresh
  } catch {
    // Private browsing. An id that changed on every request would inflate the count,
    // so fall back to nothing and simply not be counted.
    return ''
  }
}

/**
 * Ask for the room list.
 *
 * The request carries this client's id, so the poll the lobby is already
 * making doubles as the "I am here" that the online count is built from.
 */
export async function listRooms(
  relayUrl: string,
  signal?: AbortSignal,
  announce = true,
): Promise<RoomListing> {
  const url = new URL(`${relayOrigin(relayUrl)}/rooms`)
  const id = announce ? clientId() : ''
  if (id) url.searchParams.set('seen', id)
  const response = await fetch(url, { signal: signal ?? null, cache: 'no-store' })
  if (!response.ok) throw new Error(`relay returned ${response.status}`)
  const body = (await response.json()) as { rooms?: RoomSummary[]; online?: number }
  return { rooms: body.rooms ?? [], online: body.online ?? 0 }
}

export async function createRoom(
  relayUrl: string,
  name?: string,
): Promise<{ code: string; name: string }> {
  const response = await fetch(`${relayOrigin(relayUrl)}/rooms`, {
    method: 'POST',
    body: JSON.stringify(name ? { name } : {}),
  })
  if (response.status === 503) throw new Error('too many rooms are open — try one of them')
  if (!response.ok) throw new Error(`relay returned ${response.status}`)
  return (await response.json()) as { code: string; name: string }
}
